import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AppImage from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const WelcomePage = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Welcome - Radiavision Studio Gallery</title>
        <meta name="description" content="Welcome to Radiavision Studio's professional gallery management platform. Choose your access type to continue." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>

        {/* Header */}
        <header className="relative z-10 border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-4 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <AppImage
                  src="/assets/images/RVS_InitialLogo_01-02-1768324259986.png"
                  alt="RVS Logo - Professional gallery management system"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className="text-xl font-bold">Radiavision Studio</span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="relative z-10 container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-5xl mx-auto">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-primary/10 border-2 border-primary/20">
                <AppImage
                  src="/assets/images/RVS_InitialLogo_01-02-1768324259986.png"
                  alt="RVS Logo - Professional gallery management system"
                  className="w-16 h-16 object-contain"
                />
              </div>
            </div>

            {/* Heading */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                Welcome to Radiavision Studio
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Professional gallery management platform for photographers and clients
              </p>
            </div>

            {/* Choice Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* User Access Card */}
              <div className="p-8 rounded-3xl bg-card border-2 border-border shadow-warm-lg hover:shadow-warm-xl transition-all hover:scale-105">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Icon name="User" size={32} color="var(--color-primary)" />
                </div>
                <h2 className="text-2xl font-bold text-center mb-4">I'm a User</h2>
                <p className="text-muted-foreground text-center mb-6">
                  Access your galleries, view photos and videos, and download your media
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <Icon name="Check" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">View assigned galleries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="Check" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Access via gallery code</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="Check" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Download your media</span>
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/user-registration')}
                  className="w-full"
                  size="lg"
                >
                  <Icon name="LogIn" size={20} className="mr-2" />
                  User Login / Sign Up
                </Button>
              </div>

              {/* Admin Access Card */}
              <div className="p-8 rounded-3xl bg-card border-2 border-primary/30 shadow-warm-lg hover:shadow-warm-xl transition-all hover:scale-105">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Icon name="Shield" size={32} color="var(--color-primary)" />
                </div>
                <h2 className="text-2xl font-bold text-center mb-4">I'm an Owner / Contributor</h2>
                <p className="text-muted-foreground text-center mb-6">
                  Manage galleries, upload media, and track visitor analytics
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <Icon name="Check" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Create and manage galleries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="Check" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Upload and organize media</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="Check" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Track visitor engagement</span>
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/admin-login')}
                  className="w-full"
                  size="lg"
                  variant="default"
                >
                  <Icon name="Shield" size={20} className="mr-2" />
                  Admin Login
                </Button>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-16 text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Have a gallery link or code? You can access it directly through user login.
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="relative z-10 border-t border-border bg-card/50 backdrop-blur-sm mt-16">
          <div className="container mx-auto px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date()?.getFullYear()} Radiavision Studio. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default WelcomePage;